/** The knowledge base domain.
 *
 * More of this module is real than the last three, because the KB is the one
 * area with genuine write endpoints: `GET/POST /api/agent/kb/` and
 * `GET/PATCH/DELETE /api/agent/kb/{slug}/` are a complete CRUD surface over
 * title, slug, body, visibility and published state.
 *
 * What is missing is everything *around* the article — categories, tags, views,
 * ratings, versions, review workflow, scheduling and linked records. Several of
 * the reading-experience features the brief asks for are not missing at all,
 * though: a table of contents, reading time, an excerpt and related articles
 * are all derivable from the body, and are derived here rather than waited for.
 *
 * ## One thing worth knowing before designing around it
 *
 * `body_md` is named for Markdown but holds sanitised HTML — `KbArticle.save()`
 * runs it through `core.sanitize.clean_html` on every write. That allow-list
 * has no `img` and no `iframe`, so **embedded images and videos are stripped on
 * save**, silently. Any image or video feature is a sanitiser change first and a
 * UI feature second.
 */

import type { ArticleScore } from './articleFeedback'

export interface KbArticleSummary {
  slug: string
  title: string
  updated_at: string
}

export interface KbArticleDetail extends KbArticleSummary {
  body_md: string
}

/** What the **portal** returns for one article.
 *
 * `my_feedback` is this reader's own answer and lives only here. The agent API
 * returns an aggregate instead — see `AdminArticle.feedback` — because the two
 * screens ask different questions of the same model, and one shared field that
 * meant "mine" on one screen and "everyone's" on the other would be read wrong
 * on at least one of them.
 */
export interface PortalArticleDetail extends KbArticleDetail {
  my_feedback: boolean | null
}

/* ------------------------------------------------------------------ prose */

const TAG = /<[^>]*>/g
const HEADING = /<h([1-4])\b[^>]*>([\s\S]*?)<\/h\1\s*>/gi

/** The visible text of stored HTML. */
export function plainText(html: string): string {
  return html
    .replace(/<(script|style)[\s\S]*?<\/\1>/gi, '')
    .replace(TAG, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim()
}

export function wordCount(html: string): number {
  const text = plainText(html)
  return text ? text.split(/\s+/).length : 0
}

/** Minutes to read, at 200 words a minute.
 *
 *  Rounded up and floored at one: "0 min read" is not a useful thing to tell
 *  somebody, and a one-line article still costs them the click.
 */
export function readingTime(html: string): number {
  return Math.max(1, Math.ceil(wordCount(html) / 200))
}

/** First sentence or two, for a card. */
export function excerpt(html: string, max = 160): string {
  const text = plainText(html)
  if (text.length <= max) return text
  const cut = text.slice(0, max)
  const lastSpace = cut.lastIndexOf(' ')
  // Back up to the last word boundary, but only if that does not throw away
  // most of the excerpt — a single very long word should be cut mid-word rather
  // than reduced to nothing. The threshold is a proportion of `max`, not a
  // constant: hardcoding one meant a short excerpt always cut mid-word.
  const end = lastSpace > max * 0.6 ? lastSpace : max
  return `${cut.slice(0, end).trimEnd()}…`
}

/* -------------------------------------------------------- table of contents */

export interface TocEntry {
  id: string
  level: number
  text: string
}

function slugify(value: string): string {
  return (
    value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'section'
  )
}

/** Headings, with stable anchor ids.
 *
 *  Ids are generated here rather than read from the HTML because the sanitiser's
 *  attribute allow-list permits only `class` — an `id` written by an author does
 *  not survive the save. Duplicate headings get a numeric suffix so two
 *  "Prerequisites" sections do not both answer to the same anchor.
 */
export function tableOfContents(html: string): TocEntry[] {
  const entries: TocEntry[] = []
  const used = new Map<string, number>()

  for (const match of html.matchAll(HEADING)) {
    const text = plainText(match[2])
    if (!text) continue
    const base = slugify(text)
    const seen = used.get(base) ?? 0
    used.set(base, seen + 1)
    entries.push({
      id: seen === 0 ? base : `${base}-${seen + 1}`,
      level: Number(match[1]),
      text,
    })
  }

  return entries
}

/** The same HTML, with those anchor ids injected so the contents can link to
 *  them. Runs the same numbering as `tableOfContents`, so the two agree. */
export function withHeadingIds(html: string): string {
  const used = new Map<string, number>()
  return html.replace(HEADING, (_whole, level: string, inner: string) => {
    const text = plainText(inner)
    const base = slugify(text || 'section')
    const seen = used.get(base) ?? 0
    used.set(base, seen + 1)
    const id = seen === 0 ? base : `${base}-${seen + 1}`
    return `<h${level} id="${id}">${inner}</h${level}>`
  })
}

/* --------------------------------------------------------------- related */

const STOPWORDS = new Set([
  'a', 'an', 'and', 'are', 'as', 'at', 'be', 'but', 'by', 'for', 'from', 'how', 'in', 'is', 'it',
  'of', 'on', 'or', 'the', 'to', 'what', 'when', 'why', 'with', 'your', 'you',
])

function terms(value: string): Set<string> {
  return new Set(
    value
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter((word) => word.length > 2 && !STOPWORDS.has(word)),
  )
}

/** Articles whose titles share significant words with this one.
 *
 *  A stand-in, and labelled as one wherever it is shown. A real "related
 *  articles" list is curated or embedding-based; this is word overlap on titles,
 *  which is honest about being shallow and costs no backend.
 */
export function relatedArticles<T extends KbArticleSummary>(
  all: T[],
  current: KbArticleSummary,
  limit = 5,
): T[] {
  const mine = terms(current.title)
  if (mine.size === 0) return []

  return all
    .filter((article) => article.slug !== current.slug)
    .map((article) => {
      const theirs = terms(article.title)
      let shared = 0
      for (const word of mine) if (theirs.has(word)) shared += 1
      return { article, shared }
    })
    .filter((row) => row.shared > 0)
    .sort((a, b) => b.shared - a.shared || a.article.title.localeCompare(b.article.title))
    .slice(0, limit)
    .map((row) => row.article)
}

/** Most recently updated first. */
export function recentlyUpdated<T extends KbArticleSummary>(articles: T[], limit = 6): T[] {
  return [...articles]
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
    .slice(0, limit)
}

/* ----------------------------------------------------------------- admin */

export interface AdminArticle extends KbArticleDetail {
  id: number
  org_visibility: string
  published: boolean
  author_name: string | null
  created_at: string
  /** What readers said. The editor's question, not the reader's. */
  feedback?: ArticleScore
}

export interface KbStats {
  total: number
  published: number
  draft: number
  /** Published and visible to every organisation, including customers. */
  external: number
  /** Published but restricted to internal organisations. */
  internal: number
  words: number
  /** Published articles with a body under 50 words — thin enough to be a stub. */
  thin: number
}

/** Counts the editor can actually stand behind.
 *
 *  Everything here comes from fields the list endpoint returns. There is no
 *  "pending review" count because there is no review state — an article is
 *  published or it is not, and inventing a third bucket would misreport the
 *  editorial process.
 */
export function articleStats(articles: AdminArticle[]): KbStats {
  let published = 0
  let external = 0
  let internal = 0
  let words = 0
  let thin = 0

  for (const article of articles) {
    const count = wordCount(article.body_md)
    words += count
    if (!article.published) continue
    published += 1
    if (article.org_visibility === 'all') external += 1
    else internal += 1
    if (count < 50) thin += 1
  }

  return {
    total: articles.length,
    published,
    draft: articles.length - published,
    external,
    internal,
    words,
    thin,
  }
}

/* ------------------------------------------------------------- inventory */

export interface KbCapability {
  key: string
  label: string
  state: 'live' | 'derived' | 'absent'
  note: string
}

/** What the knowledge base can do, and what it cannot.
 *
 *  `derived` is its own state on purpose. A table of contents computed from the
 *  body is real and useful, but it is not the same promise as one an author
 *  curated — and the difference matters when somebody asks why a heading is
 *  missing from the contents.
 */
export const KB_CAPABILITIES: KbCapability[] = [
  { key: 'crud', label: 'Create, edit, delete', state: 'live', note: 'Full CRUD on the agent API.' },
  { key: 'publish', label: 'Draft and publish', state: 'live', note: 'A published boolean. Drafts are invisible to the portal.' },
  { key: 'visibility', label: 'Visibility', state: 'live', note: 'Two audiences: all organisations, or internal only. Enforced in one queryset manager.' },
  { key: 'search', label: 'Search', state: 'live', note: 'Title and body substring match, capped at 10 results on the portal.' },
  { key: 'author', label: 'Author', state: 'live', note: 'Set on create and returned by the agent API — but not by the portal serializer, so a reader cannot see it.' },
  { key: 'toc', label: 'Table of contents', state: 'derived', note: 'Parsed from the headings in the body. Anchor ids are generated client-side because the sanitiser strips an author-written id.' },
  { key: 'reading_time', label: 'Reading time', state: 'derived', note: 'Word count at 200 words a minute.' },
  { key: 'related', label: 'Related articles', state: 'derived', note: 'Title word overlap. A curated or embedding-based list would need a model.' },
  { key: 'recent', label: 'Recently updated', state: 'derived', note: 'Sorted on updated_at.' },
  { key: 'categories', label: 'Categories', state: 'absent', note: 'No category field. The eleven named categories need a model with an icon, description and ordering.' },
  { key: 'tags', label: 'Tags', state: 'absent', note: 'No tag field or join table.' },
  { key: 'views', label: 'View counts', state: 'absent', note: 'Nothing counts a read. Popular, most-viewed and knowledge-coverage all depend on it.' },
  { key: 'ratings', label: 'Was this helpful', state: 'live', note: 'KbArticleFeedback stores one answer per reader, the portal can set or clear it, and the agent API returns aggregate helpful/not-helpful scores.' },
  { key: 'search_analytics', label: 'Search analytics', state: 'absent', note: 'Queries are not logged, so failed searches and search success rate cannot be computed.' },
  // Corrected by the full-site audit. The note used to say the audit trail had
  // no read API; `GET /api/agent/audit/kb.KbArticle/{id}/` returns exactly this
  // history and `components/audit/EntityHistory` renders it. `live` rather than
  // `derived`: the server answers the question, the client only draws it.
  { key: 'versions', label: 'Version history', state: 'live', note: 'audit_log records every edit with before and after, and the audit API returns one article’s history. What is absent is editorial state on top of it — see Review and approval.' },
  { key: 'review', label: 'Review and approval', state: 'absent', note: 'No review state, reviewer or approval step. Published is a single boolean.' },
  { key: 'schedule', label: 'Scheduling and expiry', state: 'absent', note: 'No publish-at or expires-at. Nothing can go stale on a schedule.' },
  { key: 'attachments', label: 'Attachments', state: 'absent', note: 'Attachments exist for tickets, served through a permission-checking view. Nothing links one to an article.' },
  { key: 'media', label: 'Images and video', state: 'absent', note: 'The sanitiser allow-list has no img and no iframe, so embedded media is stripped on save. This is a sanitiser change before it is a UI feature.' },
  { key: 'links', label: 'Linked records', state: 'absent', note: 'No relationship between an article and a service, catalog item, asset, ticket, problem or change.' },
]

export const LIVE_CAPABILITIES = KB_CAPABILITIES.filter((entry) => entry.state === 'live')
export const DERIVED_CAPABILITIES = KB_CAPABILITIES.filter((entry) => entry.state === 'derived')
export const ABSENT_CAPABILITIES = KB_CAPABILITIES.filter((entry) => entry.state === 'absent')

/** The categories the knowledge base is asked to present. A specification, not
 *  data — nothing in the model carries a category. */
export const TARGET_CATEGORIES = [
  'Microsoft 365',
  'Windows',
  'Hardware',
  'Networking',
  'Security',
  'Software',
  'HR',
  'Finance',
  'Policies',
  'How-To Guides',
  'FAQs',
] as const
